/**
 * RichObjectTable is a table styled to call attention to "important objects" (usually models, or
 * something closely related), e.g. input and output samples in a stage entry.
 */

import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import {User, USER_SHAPE} from 'constants/UserConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import * as Colors from 'ui-library/Colors';
import Tooltip, {TooltipProps} from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox, {IndeterminateCheckboxProps} from 'ui-library/IndeterminateCheckbox';
import Spinner from 'ui-library/Spinner';
import {omit} from 'util/omitKey';
import {ElementMouseEvent} from 'util/Types';

import TableCollapserChevron from './tables/TableCollapserChevron';

// This should stay in sync with .richObjectTable-checkboxColumn .
export const CHECKBOX_COLUMN_WIDTH = 36;
export const CONTENT_COLUMN_MIN_WIDTH = 180;

type HeaderColor = typeof Colors.WHITE | typeof Colors.GRAY_LIGHT;

export interface RichObjectTableProps {
  children?: Array<React.ReactNode>;
  className?: string;
  collapsedHeaderText?: string;
  columns?: Array<JSX.Element>;
  minColumnWidth?: number;
  headerCells: Array<JSX.Element | string | null>;
  largerHeader?: boolean;
  isCollapsible: boolean;
  showCollapsedMessage?: boolean;
  headerColor?: HeaderColor;
  leftHeader?: JSX.Element | string;
  rightHeader?: JSX.Element | null;
  isCollapsed?: boolean;
  onToggleCollapsed?: () => void;
  useMinimalMargin?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

interface State {
  isCollapsed: boolean;
  isHovered: boolean;
}

export class Table extends React.Component<RichObjectTableProps, State> {
  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    collapsedHeaderText: PropTypes.string,
    columns: PropTypes.node,
    minColumnWidth: PropTypes.number,
    headerCells: PropTypes.arrayOf(PropTypes.node).isRequired,
    largerHeader: PropTypes.bool,
    isCollapsible: PropTypes.bool.isRequired,
    showCollapsedMessage: PropTypes.bool,
    headerColor: PropTypes.string,
    leftHeader: PropTypes.node,
    rightHeader: PropTypes.node,
    // The collapsed state can be controlled or uncontrolled.
    // Override this.state.isCollapsed if an isCollapsed prop is provided.
    isCollapsed: PropTypes.bool,
    onToggleCollapsed: PropTypes.func,
    useMinimalMargin: PropTypes.bool,
  };

  static defaultProps = {
    isCollapsible: false,
    headerColor: Colors.GRAY_LIGHT,
  };

  override state: State = {
    isCollapsed: false,
    isHovered: false,
  };

  _isTableCollapsed(): boolean {
    return this.props.isCollapsed != null ? this.props.isCollapsed : this.state.isCollapsed;
  }

  _handleToggleIsCollapsed = (): void => {
    if (this.props.onToggleCollapsed != null) {
      this.props.onToggleCollapsed();
    } else {
      this.setState({
        isCollapsed: !this.state.isCollapsed,
      });
    }
  };

  _handleMouseEnter = (): void => {
    this.setState({isHovered: true});
  };

  _handleMouseLeave = (): void => {
    this.setState({isHovered: false});
  };

  _getColumns(): Array<JSX.Element> {
    return (
      this.props.columns ||
      this.props.headerCells.map((cell, index) => (
        <col key={index} width={this.props.minColumnWidth || CONTENT_COLUMN_MIN_WIDTH} />
      ))
    );
  }

  _getApproxTableWidth(): number {
    return this.props.headerCells.length * (this.props.minColumnWidth || CONTENT_COLUMN_MIN_WIDTH);
  }

  _shouldRenderCollapserChevron(): boolean {
    if (this._isTableCollapsed()) {
      return true;
    }
    return this.props.isCollapsible && this.state.isHovered;
  }

  override render(): JSX.Element {
    const {
      className,
      collapsedHeaderText,
      leftHeader,
      rightHeader,
      headerCells,
      largerHeader,
      children,
      useMinimalMargin,
    } = this.props;
    return (
      <div
        className={classNames('richObjectTable-hoverArea', {
          'richObjectTable-minimalMargin': useMinimalMargin,
        })}
        onMouseEnter={this._handleMouseEnter}
        onMouseLeave={this._handleMouseLeave}
      >
        <div
          className={classNames(className, 'richObjectTable-container-wrapper', {
            'richObjectTable-minimalMargin': useMinimalMargin,
          })}
          data-mediocre-item-whitespace="1"
        >
          <div
            className={classNames('richObjectTable-container printable-container-scaleForPrint', {
              'is-collapsed': this._isTableCollapsed(),
            })}
            data-printable-layout-width={this._getApproxTableWidth()}
            {...getTestProps(this.props.dataTestAttrs)}
          >
            <div className="u-vertical-center-items">
              <div
                style={{backgroundColor: this.props.headerColor}}
                className={classNames('richObjectTable-header', {
                  'is-expanded': !this._isTableCollapsed(),
                  'is-collapsed': this._isTableCollapsed(),
                  'u-pointer': this.props.isCollapsible,
                })}
                data-mediocre-item-select="1"
                onDoubleClick={this.props.isCollapsible ? this._handleToggleIsCollapsed : undefined}
                {...getTestProps({component: 'RichObjectTable', element: 'header'})}
              >
                <div className="u-flex-grow">{leftHeader}</div>
                <div className="u-flex-none">
                  {this._isTableCollapsed() ? (
                    <span className="richObjectTable-collapsedHeaderText">{collapsedHeaderText}</span>
                  ) : (
                    rightHeader
                  )}
                </div>
              </div>
              {this._shouldRenderCollapserChevron() && (
                <div className="u-flex-none u-tiny-spacing-left u-vertical-center-items">
                  <TableCollapserChevron
                    showToolTip={this.props?.showCollapsedMessage}
                    isTableExpanded={!this._isTableCollapsed()}
                    onToggleCollapsed={this._handleToggleIsCollapsed}
                  />
                </div>
              )}
            </div>
            {!this._isTableCollapsed() && (
              <div className="richObjectTable-scrollContainer">
                <table className="richObjectTable table selectable">
                  <colgroup>{this._getColumns()}</colgroup>
                  <thead>
                    <Row>
                      {headerCells.map((cell, i) => (
                        <HeaderCell larger={largerHeader} key={i}>
                          {cell}
                        </HeaderCell>
                      ))}
                    </Row>
                  </thead>
                  <tbody>{children}</tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export function CheckboxCol(props: React.ColHTMLAttributes<HTMLTableColElement>): JSX.Element {
  return <col className="richObjectTable-checkboxColumn" {...props} />;
}

export function Row(props: React.HTMLAttributes<HTMLTableRowElement>): JSX.Element {
  return (
    <tr
      {...props}
      className={classNames('richObjectTable-row', props.className)}
      {...getTestProps({component: 'RichObjectTable', element: 'row'})}
    />
  );
}

type CellProps = Omit<React.TdHTMLAttributes<HTMLTableDataCellElement>, 'placeholder'> & {
  primary: boolean;
  placeholder: boolean;
};

export function Cell(props: CellProps): JSX.Element {
  const tdProps = omit(props, 'className', 'primary', 'placeholder');
  return (
    <td
      {...tdProps}
      className={classNames(
        props.className,
        props.primary ? 'richObjectTable-cell--primary' : 'richObjectTable-cell',
        {'richObjectTable-placeholderText': props.placeholder}
      )}
    />
  );
}

Cell.propTypes = {
  className: PropTypes.string,
  placeholder: PropTypes.bool.isRequired,
  primary: PropTypes.bool.isRequired,
};

Cell.defaultProps = {
  placeholder: false,
  primary: false,
};

interface HeaderCellProps extends React.ThHTMLAttributes<HTMLTableHeaderCellElement> {
  larger?: boolean;
}

export function HeaderCell(props: HeaderCellProps): JSX.Element {
  return (
    <th
      {...omit(props, 'className', 'larger')}
      className={classNames(
        {
          'richObjectTable-cell--header': !props.larger,
          'richObjectTable-cell--header-larger': props.larger,
        },
        props.className
      )}
    />
  );
}

interface AddButtonProps {
  className?: string;
  altText?: string;
  disabled: boolean;
  disabledTooltip?: string;
  onClick?: (e: ElementMouseEvent) => void;
  tooltipProps?: Omit<TooltipProps, 'isDisabled' | 'text' | 'children'>;
  dataTestAttrs?: DataTestAttrs;
}

export class AddButton extends React.Component<AddButtonProps> {
  static defaultProps = {disabled: false};

  _handleClick = (e: ElementMouseEvent): void => {
    if (this.props.disabled) {
      e.stopPropagation();
      return;
    }
    this.props.onClick!(e);
  };

  override render(): JSX.Element {
    return (
      <Tooltip
        isDisabled={!this.props.disabled || !this.props.disabledTooltip}
        text={this.props.disabledTooltip}
        disableContentSelection={true}
        {...this.props.tooltipProps}
      >
        <span
          className={classNames(this.props.className, 'richObjectTable-addButton-wrapper')}
          onClick={this._handleClick}
          {...getTestProps({component: 'AddButton', element: 'wrapper'})}
        >
          <BenchlingIcon
            className={classNames('richObjectTable-addButton', {
              'richObjectTable-addButton--disabled': this.props.disabled,
            })}
            icon="compose"
            altText={this.props.altText}
          />
        </span>
      </Tooltip>
    );
  }
}

export function CheckboxCell(props: React.InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <div className="richObjectTable-checkboxCell">
      <input type="checkbox" {...props} className={css(styles.checkbox)} />
    </div>
  );
}

export function IndeterminateCheckboxCell<T>(props: IndeterminateCheckboxProps<T>): JSX.Element {
  return (
    <div className="richObjectTable-checkboxCell">
      <IndeterminateCheckbox {...props} />
    </div>
  );
}

export function SpinningCell({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <div className="u-vertical-center-items">
      <span className="u-flex-grow">{children}</span>
      <Spinner classNames={classNames(css(styles.spinner), 'u-flex-none')} />
    </div>
  );
}

SpinningCell.propTypes = {
  children: PropTypes.node.isRequired,
};

/*
 * Render vertically centered user avatar and name
 */
export function UserCell({user}: {user: User}): JSX.Element {
  return (
    <div className="u-vertical-center-items">
      {/* Need wrapper divs around user avatar and name so flex display doesn't resize the avatar */}
      <div className="u-mini-spacing-right">
        <Avatar size={AvatarSize.XXSMALL} entity={user} />
      </div>
      <div>{user.name}</div>
    </div>
  );
}

UserCell.propTypes = {
  user: USER_SHAPE.isRequired,
};

const styles = StyleSheet.create({
  checkbox: {
    accentColor: Colors.BLUE,
  },
  spinner: {
    width: 13,
    height: 13,
    // The icon is on the right side of a container with 8px padding, so just add 4 to get 12.
    margin: '0 4px 0 12px',
  },
});
